import { useNavigate } from '@tanstack/react-router';
import { Fingerprint, KeyRound } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';
import {
  decidePasskeyOffer,
  isWebAuthnSupported,
  type PasskeyLookup,
  type PasskeyOfferDecision,
} from '../lib/passkey-offer';
import { supabase } from '../lib/supabase';
import { AuthScreen } from '@/components/auth-screen';
import { useToast } from '@/components/toast';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/** Decision for the signed-in member, from their registered passkeys. */
async function resolvePasskeyOffer(): Promise<PasskeyOfferDecision> {
  const webauthnSupported = isWebAuthnSupported();
  if (!webauthnSupported) {
    return 'skip';
  }
  const { data, error } = await supabase.auth.passkey.list();
  const lookup: PasskeyLookup =
    error === null ? { kind: 'listed', count: data.length } : { kind: 'failed' };
  return decidePasskeyOffer({ webauthnSupported, lookup });
}

/**
 * Step shown after every password sign-in (and after the invited member sets
 * their password): a member without a passkey is offered to create one on
 * this device. They may skip; the offer comes back at the next sign-in.
 */
export function PasskeyOfferPage(): JSX.Element {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [offer, setOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void resolvePasskeyOffer().then((decision) => {
      if (!active) {
        return;
      }
      if (decision === 'offer') {
        setOffer(true);
      } else {
        void navigate({ to: '/', replace: true });
      }
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  const enter = (): void => {
    void navigate({ to: '/', replace: true });
  };

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const { error: registerError } = await supabase.auth.registerPasskey();
    setBusy(false);
    if (registerError) {
      setError(registerError.message);
      return;
    }
    notify('Passkey enregistrée');
    enter();
  };

  if (!offer) {
    return (
      <AuthScreen>
        <p className="m-0 text-center text-sm text-muted-foreground">Connexion…</p>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <Card>
        <CardContent className="grid gap-5 p-6">
          <div className="grid gap-2">
            <h1 className="m-0 flex items-center gap-2 text-title">
              <Fingerprint aria-hidden="true" className="size-5" />
              Créer une passkey ?
            </h1>
            <p className="m-0 text-small text-muted-foreground">
              Vous n&apos;avez pas encore de passkey. Avec une passkey, vous vous connectez sans mot
              de passe, par empreinte, reconnaissance faciale, code de l&apos;appareil ou clé de
              sécurité.
            </p>
          </div>

          <div className="grid gap-2">
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={() => void create()}
              disabled={busy}
            >
              <KeyRound aria-hidden="true" />
              {busy ? 'Enregistrement…' : 'Créer une passkey'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="w-full"
              onClick={enter}
              disabled={busy}
            >
              Plus tard
            </Button>
          </div>

          {error !== null && <Callout variant="destructive">{error}</Callout>}

          <p className="m-0 text-caption text-muted-foreground">
            Vous pourrez aussi en ajouter une depuis les Paramètres.
          </p>
        </CardContent>
      </Card>
    </AuthScreen>
  );
}
